<template>
  <div data-cy="webhook-deliveries-tab">
    <p class="mb-4 max-w-3xl text-sm text-gray-600">
      Every event Sera sends to an endpoint is a delivery. A delivery that fails
      is retried automatically up to 8 times over about 27 hours. Deliveries are
      kept for 90 days.
    </p>

    <div
      class="mb-3 flex flex-wrap gap-2"
      role="group"
      aria-label="Filter by status"
    >
      <button
        v-for="option in statusOptions"
        :key="option.value ?? 'all'"
        type="button"
        class="rounded-full border px-3 py-1 text-xs font-medium"
        :class="
          filters.status === option.value
            ? 'border-blue-600 bg-blue-50 text-blue-700'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
        "
        :aria-pressed="filters.status === option.value"
        :data-cy="`webhook-delivery-filter:${option.value ?? 'all'}`"
        @click="setStatus(option.value)"
      >
        {{ option.label }}
      </button>
    </div>

    <form
      class="mb-4 flex flex-wrap items-end gap-3 text-sm"
      data-cy="webhook-delivery-filters"
      @submit.prevent="applyFilters"
    >
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-gray-700">Event</span>
        <select
          v-model="filters.eventType"
          class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm"
          data-cy="webhook-delivery-filter-event"
          @change="applyFilters"
        >
          <option :value="null">All events</option>
          <option
            v-for="eventType in eventTypes"
            :key="eventType.event_type"
            :value="eventType.event_type"
          >
            {{ eventType.event_type }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-gray-700">Endpoint</span>
        <select
          v-model="filters.endpointId"
          class="max-w-[14rem] rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm"
          data-cy="webhook-delivery-filter-endpoint"
          @change="applyFilters"
        >
          <option :value="null">All endpoints</option>
          <option
            v-for="endpoint in endpointOptions"
            :key="endpoint.id"
            :value="endpoint.id"
          >
            {{ endpoint.label }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-gray-700">From</span>
        <input
          v-model="filters.from"
          type="date"
          class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm"
          :max="filters.to || undefined"
          data-cy="webhook-delivery-filter-from"
          @change="applyFilters"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-gray-700">To</span>
        <input
          v-model="filters.to"
          type="date"
          class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm"
          :min="filters.from || undefined"
          data-cy="webhook-delivery-filter-to"
          @change="applyFilters"
        />
      </label>
      <label class="flex min-w-[14rem] flex-1 flex-col gap-1">
        <span class="text-xs font-medium text-gray-700">Find by id</span>
        <input
          v-model.trim="filters.q"
          type="search"
          placeholder="whmsg_…, an event id, txn_…, inv_…, cust_…"
          class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm"
          data-cy="webhook-delivery-filter-q"
        />
      </label>
      <button
        type="submit"
        class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
        data-cy="webhook-delivery-filter-apply"
      >
        Search
      </button>
      <button
        v-if="hasFilters"
        type="button"
        class="link py-2 text-sm"
        data-cy="webhook-delivery-filter-clear"
        @click="clearFilters"
      >
        Clear filters
      </button>
    </form>

    <div v-if="loading && !deliveries.length" class="flex justify-center py-10">
      <Spinner />
    </div>

    <Alert
      v-else-if="loadError"
      type="error"
      class="mb-3"
      data-cy="webhook-deliveries-error"
    >
      {{ loadError }}
      <button
        type="button"
        class="link ml-1"
        data-cy="webhook-deliveries-retry-load"
        @click="load(currentPage)"
      >
        Try again
      </button>
    </Alert>

    <p
      v-else-if="!deliveries.length"
      class="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600"
      data-cy="webhook-deliveries-empty"
    >
      {{
        hasFilters
          ? "No deliveries match these filters."
          : "No deliveries yet. They appear here as events are sent."
      }}
    </p>

    <div
      v-else
      class="overflow-x-auto rounded-lg border border-gray-200 bg-white"
    >
      <table
        class="w-full text-left text-sm text-gray-700"
        :class="{ 'opacity-60': loading }"
        data-cy="webhook-deliveries-table"
      >
        <thead class="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th class="px-3 py-2 font-semibold">Time</th>
            <th class="px-3 py-2 font-semibold">Event</th>
            <th class="px-3 py-2 font-semibold">Endpoint</th>
            <th class="px-3 py-2 font-semibold">Status</th>
            <th class="px-3 py-2 font-semibold">Attempts</th>
            <th class="px-3 py-2 font-semibold">HTTP</th>
            <th class="px-3 py-2 font-semibold">Reason</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="delivery in deliveries"
            :key="delivery.id"
            class="cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
            tabindex="0"
            :data-cy="`webhook-delivery-row:${delivery.id}`"
            @click="selected = delivery"
            @keydown.enter.prevent="selected = delivery"
          >
            <td class="whitespace-nowrap px-3 py-2">
              <span :title="formatDateTimeWithTimezone(delivery.created_at)">{{
                fromNow(delivery.created_at, true)
              }}</span>
            </td>
            <td class="whitespace-nowrap px-3 py-2">
              <code class="text-xs">{{ delivery.event_type }}</code>
              <span
                v-if="isTestDelivery(delivery)"
                class="ml-1 rounded bg-gray-100 px-1 text-[10px] font-semibold uppercase text-gray-600"
                >Test</span
              >
            </td>
            <td class="px-3 py-2">
              {{ deliveryEndpointLabel(delivery) }}
              <span
                v-if="delivery.endpoint.archived"
                class="text-xs text-gray-500"
                >(archived)</span
              >
            </td>
            <td class="px-3 py-2">
              <span
                class="whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
                :class="deliveryStatusStyle(delivery.status).pill"
                data-cy="webhook-delivery-status"
                >{{ deliveryStatusStyle(delivery.status).label }}</span
              >
            </td>
            <td class="px-3 py-2">{{ delivery.attempts_count }}</td>
            <td class="px-3 py-2">{{ delivery.last_response_code ?? "—" }}</td>
            <td class="px-3 py-2" data-cy="webhook-delivery-reason">
              {{ delivery.outcome.title }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <PaginationControls
      v-if="pagination && pagination.total_pages > 1"
      class="mt-3"
      :pager="pager"
      no-page-length
    />

    <WebhookDeliveryDrawer
      v-if="selected"
      :key="selected.id"
      :delivery="selected"
      :read-only="readOnly"
      @close="selected = null"
      @changed="replaceDelivery"
    />
  </div>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import { formatDateTimeWithTimezone } from "@/lib/helpers";
import type Pagination from "@/lib/pagination";

import PaginationControls from "@/components/PaginationControls.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import { fromNow } from "@/mixins";
import type {
  DeliveryPagination,
  DeliveryStatus,
  WebhookDeliveriesResponse,
  WebhookDelivery,
  WebhookEndpoint,
  WebhookEndpointsResponse,
  WebhookEventType
} from "@/vuex/modules/integrationWebhooks";
import WebhookDeliveryDrawer from "./WebhookDeliveryDrawer.vue";
import {
  apiError,
  DELIVERY_STATUSES,
  deliveryEndpointLabel,
  deliveryStatusStyle,
  endpointLabel,
  isTestDelivery
} from "./webhooksHub";

const props = withDefaults(
  defineProps<{
    eventTypes: WebhookEventType[];
    readOnly?: boolean;
    initialEndpointId?: string | null;
  }>(),
  { readOnly: false, initialEndpointId: null }
);

const store = useStore();

const PER_PAGE = 25;

const filters = reactive<{
  status: DeliveryStatus | null;
  eventType: string | null;
  endpointId: string | null;
  from: string;
  to: string;
  q: string;
}>({
  status: null,
  eventType: null,
  endpointId: props.initialEndpointId,
  from: "",
  to: "",
  q: ""
});

const deliveries = ref<WebhookDelivery[]>([]);
const pagination = ref<DeliveryPagination | null>(null);
const endpoints = ref<WebhookEndpoint[]>([]);
const loading = ref(false);
const loadError = ref("");
const selected = ref<WebhookDelivery | null>(null);

const statusOptions = [
  { value: null, label: "All" },
  ...DELIVERY_STATUSES.map((value) => ({
    value,
    label: deliveryStatusStyle(value).label
  }))
];

const endpointOptions = computed(() => {
  const options = endpoints.value.map((e) => ({
    id: e.id,
    label: endpointLabel(e)
  }));
  // A link from an endpoint the list no longer has (archived) still filters.
  if (
    filters.endpointId &&
    !options.some((option) => option.id === filters.endpointId)
  ) {
    options.push({ id: filters.endpointId, label: filters.endpointId });
  }
  return options;
});

const hasFilters = computed(
  () =>
    !!(
      filters.status ||
      filters.eventType ||
      filters.endpointId ||
      filters.from ||
      filters.to ||
      filters.q
    )
);

const currentPage = computed(() => pagination.value?.page ?? 1);

// PaginationControls only reads meta.pagination and calls goToPage; the API
// takes plain params, so this stands in for lib/pagination, as in the
// Zapier deliveries drawer.
const pager = computed(
  () =>
    ({
      meta: { pagination: pagination.value },
      goToPage: async (page: number | string) => {
        await load(Number(page));
        return null;
      }
    }) as unknown as Pagination
);

// Dates are whole days in the account's time zone: `since` is the start of
// From, `until` (exclusive) the start of the day after To.
function bounds() {
  return {
    since: filters.from
      ? moment(filters.from, "YYYY-MM-DD").startOf("day").toISOString()
      : null,
    until: filters.to
      ? moment(filters.to, "YYYY-MM-DD")
          .add(1, "day")
          .startOf("day")
          .toISOString()
      : null
  };
}

// A filter or page change can overtake a slower earlier request; only the
// latest one may write.
let requestSeq = 0;

async function load(page = 1) {
  const seq = ++requestSeq;
  loading.value = true;
  try {
    const response: WebhookDeliveriesResponse = await store.dispatch(
      "fetchWebhookDeliveries",
      {
        status: filters.status,
        eventType: filters.eventType,
        endpointId: filters.endpointId,
        q: filters.q || null,
        ...bounds(),
        page,
        perPage: PER_PAGE
      }
    );
    if (seq !== requestSeq) return;
    deliveries.value = response.data;
    pagination.value = response.meta.pagination;
    loadError.value = "";
  } catch (error) {
    if (seq !== requestSeq) return;
    deliveries.value = [];
    pagination.value = null;
    loadError.value = apiError(error, "Deliveries couldn't be loaded.").message;
  } finally {
    if (seq === requestSeq) loading.value = false;
  }
}

function applyFilters() {
  load(1);
}

function setStatus(value: DeliveryStatus | null) {
  if (filters.status === value) return;
  filters.status = value;
  load(1);
}

function clearFilters() {
  Object.assign(filters, {
    status: null,
    eventType: null,
    endpointId: null,
    from: "",
    to: "",
    q: ""
  });
  load(1);
}

function replaceDelivery(updated: WebhookDelivery) {
  const index = deliveries.value.findIndex((d) => d.id === updated.id);
  if (index !== -1) deliveries.value.splice(index, 1, updated);
}

async function loadEndpoints() {
  try {
    const response: WebhookEndpointsResponse = await store.dispatch(
      "fetchWebhookEndpoints"
    );
    endpoints.value = response.data;
  } catch {
    // The endpoint filter falls back to ids; the deliveries still load.
  }
}

onMounted(() => {
  load(1);
  loadEndpoints();
});
</script>
